import React, { useState, useMemo, useContext } from "react";
import {
    Box,
    Typography,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    Divider,
    Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import {
    DmsAltBox,
    DmsButton,
    DmsPage,
    DmsTable,
    intl,
    LanguageBaseContext,
} from "dmsbasereact";
import { ReportFilters } from "../ReportsPage/components/ReportFilters";
import { useReport } from "../ReportsPage/components/useReport";
import ExportStartedDialog from "../../services/AsyncExport/ExportStartedDialog/ExportStartedDialog";
import { EXPORT_JOB_TYPE } from "../../services/AsyncExport/constants";

export const AdvancedAuditPage = () => {
    const { language } = useContext(LanguageBaseContext);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [pendingFormat, setPendingFormat] = useState("pdf");
    const [pendingFilters, setPendingFilters] = useState([]);
    const [pendingHiddenColumns, setPendingHiddenColumns] = useState([]);

    const handleAsyncExport = (format, filtersArray, hiddenColumns) => {
        setPendingFormat(format || "pdf");
        setPendingFilters(filtersArray || []);
        setPendingHiddenColumns(hiddenColumns || []);
        setExportDialogOpen(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    const header = useMemo(() => [
        {
            label: intl.get("ID").d("ID"),
            column_name: "id",
            width: "80px",
        },
        {
            label: intl.get("DateTime").d("Date/Time"),
            column_name: "created_on",
            width: "160px",
        },
        {
            label: intl.get("User").d("User"),
            column_name: "user_name",
            width: "150px",
        },
        {
            label: intl.get("Email").d("Email"),
            column_name: "user_email",
            width: "180px",
        },
        {
            label: intl.get("Profile").d("Profile"),
            column_name: "user_profile",
            width: "120px",
        },
        {
            label: intl.get("Origin").d("Origin (IP)"),
            column_name: "ipv4",
            width: "120px",
        },
        {
            label: intl.get("Level").d("Level"),
            column_name: "level",
            width: "80px",
        },
        {
            label: intl.get("Type").d("Type"),
            column_name: "type",
            width: "80px",
        },
        {
            label: intl.get("Action").d("Action"),
            column_name: "module",
            width: "150px",
            function: (value) => {
                if (!value) return "--";
                return intl.get(`AuditModule.${value}`).d(value);
            },
        },
        {
            label: intl.get("Description").d("Description"),
            column_name: "description",
            width: "250px",
            function: (value) => {
                if (!value) return "--";
                return intl.get(`AuditDescription.${value}`).d(value);
            },
        },
    ], [language]);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    const filterFields = useMemo(() => [
        {
            name: "user_name",
            label: intl.get("User").d("User"),
            type: "text",
        },
        {
            name: "user_email",
            label: intl.get("Email").d("Email"),
            type: "text",
        },
        {
            name: "description",
            label: intl.get("Description").d("Description"),
            type: "text",
        },
        {
            name: "level",
            label: intl.get("Level").d("Level"),
            type: "multiselect",
            optionsKey: "level",
        },
        {
            name: "type",
            label: intl.get("Type").d("Type"),
            type: "multiselect",
            optionsKey: "type",
        },
    ], [language]);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    const detailLabels = useMemo(() => ({
        id: "ID",
        created_on: intl.get("DateTime").d("Date/Time"),
        user_name: intl.get("User").d("User"),
        user_email: intl.get("Email").d("Email"),
        user_profile: intl.get("Profile").d("Profile"),
        ipv4: intl.get("Origin").d("Origin (IP)"),
        level: intl.get("Level").d("Level"),
        type: intl.get("Type").d("Type"),
        module: intl.get("Action").d("Action"),
        description: intl.get("Description").d("Description"),
    }), [language]);

    const {
        state: { tableRef, filters, clearKey, filterFields: processedFilterFields, columnVisibility },
        handlers: {
            setFilters,
            handleApplyFilters,
            handleClearFilters,
            handleRefresh,
            buildPreFilter,
            buildExportUrl,
            setColumnVisibility,
        },
    } = useReport({
        filterOptionsUrl: "/reports/audit-trail/filter-options",
        exportUrl: "/reports/audit-trail/export",
        filterFields: filterFields,
    });

    const handleViewDetails = (row) => {
        setSelectedRow(row);
        setDetailsOpen(true);
    };

    const formatPayload = (data) => {
        if (!data || data === "None" || data === "--") {
            return null;
        }
        try {
            const parsed = JSON.parse(data);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return data;
        }
    };

    const headerWithDetails = useMemo(() => {
        const detailsColumn = {
            label: intl.get("Details").d("Details"),
            column_name: "details",
            width: "80px",
            sortable: false,
            function: (value, row) => (
                <Tooltip title={intl.get("Tooltip.Action.ViewDetails").d("Ver detalhes")}>
                    <IconButton
                        size="small"
                        onClick={() => handleViewDetails(row)}
                        color="primary"
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        };
        return [...header, detailsColumn];
    }, [header]);

    const visibleHeader = useMemo(() => {
        return headerWithDetails.filter(
            (col) => columnVisibility[col.column_name] !== false
        );
    }, [headerWithDetails, columnVisibility]);

    const pageTitle = intl.get("AuditTrail").d("Audit Trail");

    const renderDetailValue = (value) => {
        if (value === null || value === undefined || value === "") {
            return "--";
        }
        return String(value);
    };

    const renderDetailFieldValue = (key, value) => {
        if (value === null || value === undefined || value === "") return "--";
        if (key === "module") {
            return intl.get(`AuditModule.${value}`).d(String(value));
        }
        if (key === "description") {
            return intl.get(`AuditDescription.${value}`).d(String(value));
        }
        return renderDetailValue(value);
    };

    const parsePayloadChanges = (data) => {
        if (!data || data === "None" || data === "--") return null;
        try {
            const parsed = JSON.parse(data);
            if (parsed && Array.isArray(parsed.changes) && parsed.changes.length > 0) {
                return parsed;
            }
            if (parsed && typeof parsed.changes === "object" && !Array.isArray(parsed.changes)) {
                const changesArray = Object.entries(parsed.changes).map(([field, vals]) => ({
                    field,
                    old: vals.old,
                    new: vals.new,
                }));
                if (changesArray.length > 0) {
                    return { ...parsed, changes: changesArray };
                }
            }
        } catch {
            // ignore
        }
        return null;
    };

    const translateFieldName = (field) => {
        const key = `AuditField.${field}`;
        const translated = intl.get(key).d(null);
        if (translated) return translated;
        return field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const renderChangesTable = (changes) => (
        <Table size="small" sx={{ mb: 1 }}>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                        {intl.get("Field").d("Field")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                        {intl.get("Before").d("Before")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                        {intl.get("After").d("After")}
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {changes.map((c, i) => (
                    <TableRow key={i}>
                        <TableCell>{translateFieldName(c.field)}</TableCell>
                        <TableCell sx={{ color: "error.main" }}>
                            {c.old !== null && c.old !== undefined ? String(c.old) : "--"}
                        </TableCell>
                        <TableCell sx={{ color: "success.main" }}>
                            {c.new !== null && c.new !== undefined ? String(c.new) : "--"}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    return (
        <DmsPage name={pageTitle} manual_slug="audit-trail">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                            {pageTitle}
                        </Typography>
                        <ReportFilters
                            filterFields={processedFilterFields}
                            filters={filters}
                            onFiltersChange={setFilters}
                            onApply={handleApplyFilters}
                            onClear={handleClearFilters}
                            onRefresh={handleRefresh}
                            exportUrl={buildExportUrl()}
                            columns={headerWithDetails}
                            columnVisibility={columnVisibility}
                            onColumnVisibilityChange={setColumnVisibility}
                            showPdf={true}
                            onAsyncExport={handleAsyncExport}
                        />
                    </Stack>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTable
                        key={`audit-table-${clearKey}`}
                        ref={tableRef}
                        header={visibleHeader}
                        url="/reports/audit-trail/table"
                        managed={true}
                        needs_authentication={true}
                        pre_filter={buildPreFilter()}
                        toolbar={<></>}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h6">
                            {intl.get("ActionDetails").d("Action Details")}
                            {selectedRow?.id && ` #${selectedRow.id}`}
                        </Typography>
                        <Tooltip title={intl.get("Tooltip.Action.Close").d("Close")}>
                            <IconButton
                                onClick={() => setDetailsOpen(false)}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedRow && (
                        <Stack spacing={2}>
                            <Table size="small">
                                <TableBody>
                                    {Object.entries(detailLabels).map(([key, label]) => (
                                        <TableRow key={key}>
                                            <TableCell
                                                component="th"
                                                sx={{
                                                    fontWeight: "bold",
                                                    width: "150px",
                                                    backgroundColor: "#f5f5f5",
                                                }}
                                            >
                                                {label}
                                            </TableCell>
                                            <TableCell>
                                                {renderDetailFieldValue(key, selectedRow[key])}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            <Divider />
                            <Typography variant="subtitle1" fontWeight="bold">
                                {intl.get("Payload").d("Payload")}
                            </Typography>
                            {selectedRow.data && selectedRow.data !== "--" && selectedRow.data !== "None" ? (
                                <>
                                    {(() => {
                                        const parsed = parsePayloadChanges(selectedRow.data);
                                        if (parsed && parsed.changes) {
                                            const { changes, ...rest } = parsed;
                                            return (
                                                <>
                                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                                        {intl.get("Changes").d("Changes")}
                                                    </Typography>
                                                    {renderChangesTable(changes)}
                                                    {Object.keys(rest).length > 0 && (
                                                        <Box
                                                            component="pre"
                                                            sx={{
                                                                backgroundColor: "#f5f5f5",
                                                                p: 2,
                                                                borderRadius: 1,
                                                                overflow: "auto",
                                                                maxHeight: "200px",
                                                                fontSize: "0.85rem",
                                                                fontFamily: "monospace",
                                                                margin: 0,
                                                            }}
                                                        >
                                                            {JSON.stringify(rest, null, 2)}
                                                        </Box>
                                                    )}
                                                </>
                                            );
                                        }
                                        return (
                                            <Box
                                                component="pre"
                                                sx={{
                                                    backgroundColor: "#f5f5f5",
                                                    p: 2,
                                                    borderRadius: 1,
                                                    overflow: "auto",
                                                    maxHeight: "300px",
                                                    fontSize: "0.85rem",
                                                    fontFamily: "monospace",
                                                    margin: 0,
                                                }}
                                            >
                                                {formatPayload(selectedRow.data)}
                                            </Box>
                                        );
                                    })()}
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                    {intl.get("NoPayload").d("No payload registered for this action.")}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <DmsButton
                        variant="secondary"
                        onClick={() => setDetailsOpen(false)}
                    >
                        {intl.get("Close").d("Close")}
                    </DmsButton>
                </DialogActions>
            </Dialog>

            <ExportStartedDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                jobType={EXPORT_JOB_TYPE.AUDIT_TRAIL}
                format={pendingFormat}
                filtersArray={pendingFilters}
                hiddenColumns={pendingHiddenColumns}
            />
        </DmsPage>
    );
};

export default AdvancedAuditPage;
